import { Injectable } from '@angular/core';
import { HttpClient, HttpHeaders } from '@angular/common/http';

import { map } from 'rxjs/operators';

import { OauthService } from './oauth.service';
import {Package, Rule} from './capd.model';
import { APIURL } from './url.config';
import { throwError } from "rxjs";

/**
 * Service class that retrieves and manipulates the collection of rules.
 */
@Injectable()
export class RulesService {

  constructor(private http: HttpClient,
              private auth: OauthService) {
  }

  /**
   * Utility function that adds an extra "." at the end of
   * the string, to help with processing.
   *
   * @func dotOverride
   * @param {string} ruleId - the given rule ID.
   * @returns {string} the rule ID with a ".+" at the end.
   */
  private static dotOverride(ruleId: string): string {

    if (ruleId.indexOf('.') < 0) { return ruleId; }
    else { return ruleId + '.+'; }
  }

  private static handleError(error: Response) {
    console.error(error);
    return throwError(error || 'Server error');
  }

  /**
   * Loads the complete list of library rules.
   *
   * @async
   * @func getRules
   * @returns {Promise<Rule[]>} a list of all rules in the library.
   */
  getRules() {
    return this.auth.getToken().then(token => {
      const authorization = 'bearer ' + token;
      const options = { headers: new HttpHeaders({Authorization : authorization }) };

      return this.http.get<Rule[]>(APIURL + 'api/contentlibrary/rules', options)
        .toPromise()
        .catch(RulesService.handleError);
    })
    .catch(RulesService.handleError);
  }

  /**
   * Loads the complete list of library rules from postgres.
   *
   * @async
   * @func getRules
   * @returns {Promise<Rule[]>} a list of all rules in the library.
   */
  getRulesV2() {
    return this.auth.getToken().then(token => {
      const authorization = 'bearer ' + token;
      const options = { headers: new HttpHeaders({Authorization : authorization }) };

      return this.http.get<Rule[]>(APIURL + 'api/contentlibrary/v2', options)
          .toPromise()
          .catch(RulesService.handleError);
    })
        .catch(RulesService.handleError);
  }

  /**
   * Loads a single rule from the root library based on trigger code from postgres.
   *
   * @async
   * @func getRules
   * @param triggerCode the trigger code of the requested rule
   * @returns {Promise<Rule>} a rule in the library.
   */
  getRuleByTriggerCodeV2(triggerCode) {
    return this.auth.getToken().then(token => {
      const authorization = 'bearer ' + token;
      const options = { headers: new HttpHeaders({Authorization : authorization }) };

      return this.http.get<Rule>(APIURL + 'api/contentlibrary/v2/' + triggerCode, options)
          .toPromise()
          .catch(RulesService.handleError);
    })
        .catch(RulesService.handleError);
  }

  /**
   * Get all rules that have not been added to a given package.
   *
   * @async
   * @func getPackageRules
   * @returns {Promise<Rule[]>} a list of rules not in the package yet.
   */
  getPackageRules(pack) {
    return this.auth.getToken().then(token => {
      const packId = pack._id;
      const authorization = 'bearer ' + token;
      const options = { headers: new HttpHeaders({Authorization : authorization }) };

      return this.http.get<Rule[]>(APIURL + 'api/contentpackages/' +
                           packId + '/libraryrules', options)
        .toPromise()
        .catch(RulesService.handleError);
    })
    .catch(RulesService.handleError);
  }

  /** Get all rules assigned to a given group
   *
   * @async
   * @func getGroupRules
   * @returns {Promise<Rule[]>} a list of rules assigned to the group.
   */
  getGroupRules(root, group) {
    return this.auth.getToken().then(token => {
      const url = (APIURL + 'api/contentpackages/orgs/' + root + '/groupOids/' +
                 root + '%2B' + group + '/rules');
      const authorization = 'bearer ' + token;
      const options = { headers: new HttpHeaders({Authorization : authorization }) };

      return this.http.get<Rule[]>(url, options)
        .toPromise()
        .catch(RulesService.handleError);
    })
    .catch(RulesService.handleError);
  }

  getGroupContentV2(groupId) {
    return this.auth.getToken().then(token => {
      const authorization = 'bearer ' + token;
      const options = { headers: new HttpHeaders({
          'Content-Type': 'application/json',
          Authorization : authorization,
        }) };

      return this.http.get<Rule[]>(APIURL + 'api/groups/v2/' + groupId + '/content', options)
          .toPromise()
          .catch(RulesService.handleError);
    })
        .catch(RulesService.handleError);
  }

  getAllContent(triggerSilent?) {
    return this.auth.getToken().then(token => {
      const authorization = 'bearer ' + token;
      const options = { headers: new HttpHeaders({
          'Content-Type': 'application/json',
          Authorization : authorization
        }) };

      const optionsWithTriggerSilentParam = { headers: new HttpHeaders({
          'Content-Type': 'application/json',
          Authorization : authorization,
          isSilent: triggerSilent
        }) };
      if (triggerSilent != null && triggerSilent != 'null') {
        return this.http.get<Rule[]>(APIURL + 'api/groups/v2/all', optionsWithTriggerSilentParam)
            .toPromise()
            .catch(RulesService.handleError);
      } else {
        return this.http.get<Rule[]>(APIURL + 'api/groups/v2/all', options)
            .toPromise();
      }
    })
  }

  /** Get all rules assigned to a given group along with their relevant details
   *
   * @async
   * @func getGroupRules
   * @returns {Promise<Rule[]>} a list of rules assigned to the group along with their relevant details.
   */
  getGroupRulesFullDetails(root, group) {
    return this.auth.getToken().then(token => {
      const url = (APIURL + 'api/contentpackages/orgs/' + root + '/groupOids/' +
          root + '%2B' + group + '/rulesList');
      const authorization = 'bearer ' + token;
      const options = { headers: new HttpHeaders({Authorization : authorization }) };

      return this.http.get<Rule[]>(url, options)
          .toPromise()
          .catch(RulesService.handleError);
    })
        .catch(RulesService.handleError);
  }

  /**
   * Gets a list of all category names
   *
   * @async
   * @func getCategories
   * @returns {Promise<string[]>} A string list of categories
   */
  getCategories() {
    return this.auth.getToken().then(token => {
      const authorization = 'bearer ' + token;
      const options = { headers: new HttpHeaders({Authorization : authorization }) };

      return this.http.get<string[]>(APIURL + 'api/contentlibrary/rules/categories', options)
            .toPromise()
            .catch(RulesService.handleError);
    })
    .catch(RulesService.handleError);
  }

  /**
   * Adds a new rule to the main library
   *
   * @async
   * @func addRule
   * @param {Rule} newRule - the rule to add.
   */
  addRule(newRule) {
    return this.auth.getToken().then(token => {
      const body = JSON.stringify(newRule);
      const authorization = 'bearer ' + token;
      const options = { headers: new HttpHeaders({
        'Content-Type': 'application/json',
        Authorization : authorization,
      }) };

      return this.http.post(APIURL + 'api/contentlibrary/rules', body, options)
        .toPromise()
        .catch(RulesService.handleError);
    });
  }

  /**
   * Updates the data of an existing rule
   *
   * @async
   * @func updateRule
   * @param {Rule} updatedRule - the rule to update.
   */
  updateRule(updatedRule) {
    return this.auth.getToken().then(token => {
      const url = APIURL + 'api/contentlibrary/rules/' + RulesService.dotOverride(updatedRule._id);
      const body = JSON.stringify(updatedRule);
      const authorization = 'bearer ' + token;
      const options = { headers: new HttpHeaders({
        'Content-Type': 'application/json',
        Authorization : authorization,
      }) };

      return this.http.put(url, body, options)
        .toPromise()
        .catch(RulesService.handleError);
    })
    .catch(RulesService.handleError);
  }

  /**
   * Updates the data of an existing rule
   *
   * @async
   * @func updateRule
   * @param {Rule} updatedRule - the rule to update.
   */
  updateRuleV2(updatedRule) {
    return this.auth.getToken().then(token => {
      const url = APIURL + 'api/contentlibrary/v2/' + RulesService.dotOverride(updatedRule.triggerCode);
      const body = JSON.stringify(updatedRule);
      const authorization = 'bearer ' + token;
      const options = { headers: new HttpHeaders({
          'Content-Type': 'application/json',
          Authorization : authorization,
        }) };

      return this.http.post(url, body, options)
          .toPromise()
          .catch(RulesService.handleError);
    })
        .catch(RulesService.handleError);
  }

  /**
   * Saves a customized version of a rule for a given organization
   *
   * @async
   * @func saveCustomRule
   * @param {Rule} customRule - the customized version of a rule.
   * @param {string} orgId - the org to associate the customized rule with.
   */
  saveCustomRule(customRule, orgId) {
    return this.auth.getToken().then(token => {
      const body = JSON.stringify(customRule);
      const authorization = 'bearer ' + token;
      const url = APIURL + 'api/contentpackages/' +
        customRule.contentPackageId + '/orgs/' + orgId + '/rules/';
      const options = { headers: new HttpHeaders({
        'Content-Type': 'application/json',
        Authorization : authorization,
      }) };

      return this.http.post(url, body, options)
        .toPromise()
        .catch(RulesService.handleError);
    })
    .catch(RulesService.handleError);
  }

  /**
   * Gets a customized version of a rule for a given organization.
   *
   * @async
   * @func getCustomRule
   * @param {string} packageId - the package the rule is associated with.
   * @param {string} ruleId - the ID of the rule to look up.
   * @param {string} orgId - the organization the rule belongs to.
   * @returns {Promise<Rule>} the customized rule, or null if there is no custom rule.
   */
  getCustomRule(packageId, ruleId, orgId) {
    return this.auth.getToken().then(token => {
      const authorization = 'bearer ' + token;
      const url = APIURL + 'api/contentpackages/' + packageId + '/orgs/' +
                orgId + '/rules/' + RulesService.dotOverride(ruleId);
      const options = { headers: new HttpHeaders({
        'Content-Type': 'application/json',
        Authorization : authorization,
      }) };

      return this.http.get<Rule>(url, options)
        .toPromise()
        .catch(RulesService.handleError);
    })
    .catch(RulesService.handleError);
  }

  /**
   * Deletes a rule.
   *
   * @async
   * @func deleteRule
   * @param {any} badRule - either a string of the rule ID to remove, or the Rule object.
   */
  deleteRule(badRule) {
    return this.auth.getToken().then(token => {
      const authorization = 'bearer ' + token;
      let url = '';
      if (typeof(badRule) === 'string') {
        url = APIURL + 'api/contentlibrary/rules/' + RulesService.dotOverride(badRule);
      }
      else {
        url = APIURL + 'api/contentlibrary/rules/' + RulesService.dotOverride(badRule._id);
      }
      const options = { headers: new HttpHeaders({Authorization : authorization }) };

      return this.http.delete(url, options)
        .pipe(map(res => console.log(res)))
        .toPromise()
        .catch(RulesService.handleError);
    })
    .catch(RulesService.handleError);
  }
}
